/**
 * 📋 ORDER HISTORY PAGE
 * View all past orders
 */

import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Clock, CheckCircle, Loader } from 'lucide-react';
import { useOrderStore, Order } from '../store';
import styles from './OrderHistory.module.css';

// Map item IDs to their images
const itemImages: Record<string, string> = {
  'FM001': '/softserve-tran.png',
  'FM002': '/softserve-swirl.png',
  'FM003': '/softserve-transchocdip.png',
  'FM004': '/softserve-tranvanillalicious.png',
};

const getItemImage = (menuItemId: string) => {
  return itemImages[menuItemId] || '/softserve-tran.png';
};

// Topping names for display
const TOPPINGS: Record<string, string> = {
  'choc_sprinkles': 'Choc Sprinkles',
  'rainbow_sprinkles': 'Rainbow Sprinkles',
  'crushed_nuts': 'Crushed Nuts',
  'hot_fudge': 'Hot Fudge',
  'caramel': 'Caramel',
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
  });
};

const getStatusIcon = (status: Order['status']) => {
  switch (status) {
    case 'ready':
    case 'collected':
      return <CheckCircle size={16} className={styles.statusIconDone} />;
    case 'preparing':
      return <Loader size={16} className={styles.statusIconProgress} />;
    default:
      return <Clock size={16} className={styles.statusIconPending} />;
  }
};

const getStatusText = (status: Order['status']) => {
  switch (status) {
    case 'ready': return 'Ready for pickup';
    case 'collected': return 'Completed';
    case 'preparing': return 'Preparing';
    default: return 'Pending';
  }
};

export function OrderHistoryPage() {
  const navigate = useNavigate();
  const orderHistory = useOrderStore((state) => state.orderHistory);

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowLeft size={24} />
        </button>
        <h1 className={styles.title}>Order History</h1>
        <div style={{ width: 24 }} />
      </header>

      {/* Orders List */}
      {orderHistory.length === 0 ? (
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}>📋</span>
          <h2 className={styles.emptyTitle}>No orders yet</h2>
          <p className={styles.emptySubtitle}>Your order history will appear here</p>
          <button
            className={styles.browseButton}
            onClick={() => navigate('/menu')}
          >
            Browse Menu
          </button>
        </div>
      ) : (
        <div className={styles.ordersList}>
          {orderHistory.map((order) => (
            <button
              key={order.id}
              className={styles.orderCard}
              onClick={() => navigate(`/order/${order.id}`)}
            >
              {/* Order Header */}
              <div className={styles.orderHeader}>
                <div className={styles.orderInfo}>
                  <span className={styles.orderNumber}>Order #{order.orderNumber}</span>
                  <span className={styles.orderDate}>{formatDate(order.createdAt)}</span>
                </div>
                <div className={styles.orderStatus}>
                  {getStatusIcon(order.status)}
                  <span className={styles.statusText}>{getStatusText(order.status)}</span>
                </div>
              </div>

              {/* Order Items - Full Details */}
              <div className={styles.itemsDetail}>
                {order.items.map((item, index) => (
                  <div key={item.id || index} className={styles.itemRow}>
                    <div className={styles.itemThumbSingle}>
                      <img
                        src={getItemImage(item.menuItemId)}
                        alt={item.name}
                        className={styles.itemImg}
                      />
                    </div>
                    <div className={styles.itemDetails}>
                      <div className={styles.itemMainRow}>
                        <span className={styles.itemQty}>{item.quantity}x</span>
                        <span className={styles.itemName}>{item.name}</span>
                      </div>
                      <div className={styles.itemMeta}>
                        {item.size && (
                          <span className={styles.itemTag}>
                            {item.size === 'large' ? 'Large' : 'Small'}
                          </span>
                        )}
                        {item.container && (
                          <span className={styles.itemTag}>
                            {item.container === 'cone' ? '🍦 Cone' : '🥤 Cup'}
                          </span>
                        )}
                      </div>
                      {item.toppings && item.toppings.length > 0 && (
                        <div className={styles.itemToppings}>
                          {item.toppings.map((t, i) => (
                            <span key={i} className={styles.toppingTag}>
                              + {TOPPINGS[t] || t}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <span className={styles.itemPrice}>
                      ${((item.price + (item.toppings?.length || 0) * 1.5) * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Order Footer */}
              <div className={styles.orderFooter}>
                <span className={styles.orderTotal}>${order.total.toFixed(2)}</span>
                <ChevronRight size={20} className={styles.chevron} />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
